import React, { useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Tooltip from "@mui/material/Tooltip";
import { get_t_str_shortened, formatName } from "../../../../helpers/helpers";
import {
  SUMMARY_CARD_REGISTRY,
  DEFAULT_CARD_ORDER,
} from "./summaryCardRegistry";

const day_nums = [0, 1, 2, 3, 4, 5, 6];

function buildShiftDetail(violationShift, scheduleById) {
  const sched = scheduleById[violationShift?.id];
  if (!sched || sched.shift_start == null || sched.shift_end == null) {
    return null;
  }
  const start = get_t_str_shortened(sched.shift_start);
  const end = get_t_str_shortened(sched.shift_end);
  const breaks = [];
  if ((sched.break_length || 0) > 0 && sched.break_start != null) {
    breaks.push(
      `${get_t_str_shortened(sched.break_start)}–${get_t_str_shortened(
        sched.break_start + sched.break_length
      )}`
    );
  }
  if ((sched.break_length_2 || 0) > 0 && sched.break_start_2 != null) {
    breaks.push(
      `${get_t_str_shortened(sched.break_start_2)}–${get_t_str_shortened(
        sched.break_start_2 + sched.break_length_2
      )}`
    );
  }
  (sched.paid_breaks || []).forEach((b) => {
    if ((b.break_length || 0) > 0 && b.break_start != null) {
      breaks.push(
        `${get_t_str_shortened(b.break_start)}–${get_t_str_shortened(
          b.break_start + b.break_length
        )} (paid)`
      );
    }
  });
  return { timeRange: `${start}–${end}`, breaks };
}

const REG_TYPE_NAMES = {
  daily_overtime: "Daily Overtime",
  weekly_overtime: "Weekly Overtime",
  meal_break: "Meal Break",
  seventh_day_overtime: "7th Day Overtime",
  shift_separation: "Shift Separation",
  rest_break: "Rest Break",
  meal_break_timing: "Meal Break Timing",
  rest_break_timing: "Rest Break Timing",
  split_shift: "Split Shift",
  minor_work_hours_14_15: "Minor Hours (14–15)",
  minor_work_hours_16_17: "Minor Hours (16–17)",
};

function getRegulationDisplayName(v) {
  const title = v.title;
  if (title) {
    let m = title.match(/^Missing Required (.+)$/);
    if (m) return `${m[1]} Missing`;
    m = title.match(/^Insufficient (.+) Duration$/);
    if (m) return `${m[1]} Too Short`;
    m = title.match(/^Employee in (.+)$/);
    if (m) return m[1];
    m = title.match(/^Approaching (.+)$/);
    if (m) return `${m[1]} Approaching`;
    return title;
  }
  const base = REG_TYPE_NAMES[v.regulation_type] || v.regulation_type;
  if (v.regulation_type === "meal_break" && v.details?.break_index === 2) {
    return "Second Meal Break";
  }
  return base;
}

const lightTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: "white",
      color: "#1e293b",
      border: "1px solid #e2e8f0",
      borderRadius: "10px",
      boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
      p: 0,
      maxWidth: "none",
    },
  },
  arrow: {
    sx: {
      color: "white",
      "&::before": {
        border: "1px solid #e2e8f0",
        backgroundColor: "white",
        boxSizing: "border-box",
      },
    },
  },
};

function ViolationTooltip({ dateLabel, violations }) {
  const count = violations.length;
  return (
    <div className="px-3 py-2.5 min-w-[260px] max-h-[min(70vh,560px)] overflow-y-auto">
      <div className="text-[11px] font-semibold text-slate-900">
        {dateLabel}
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5 mb-2">
        {count === 0
          ? "No violations"
          : `${count} ${count === 1 ? "violation" : "violations"}`}
      </div>
      {count > 0 && (
        <div className="flex flex-col gap-2">
          {violations.map((v, idx) => (
            <div
              key={idx}
              className="border-l-2 border-rose-300 pl-2 py-0.5"
            >
              <div className="text-xs font-medium text-slate-900 leading-tight">
                {v.employeeName}
              </div>
              <div className="text-[11px] text-slate-500 leading-tight">
                {v.regulationName}
              </div>
              {v.shifts && v.shifts.length > 0 && (
                <div className="mt-1 flex flex-col gap-0.5">
                  {v.shifts.map((s, si) => (
                    <div
                      key={si}
                      className="text-[11px] text-slate-700 leading-tight"
                    >
                      <span className="font-medium">{s.timeRange}</span>
                      {s.breaks && s.breaks.length > 0 && (
                        <span className="text-slate-400">
                          {" · break "}
                          {s.breaks.join(", ")}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {v.unresolvedShiftCount > 0 && (
                <div className="text-[11px] text-slate-400 italic leading-tight mt-0.5">
                  {v.unresolvedShiftCount}{" "}
                  {v.unresolvedShiftCount === 1 ? "shift" : "shifts"} at another
                  store
                </div>
              )}
              {(!v.shifts || v.shifts.length === 0) &&
                !v.unresolvedShiftCount &&
                v.detail && (
                  <div className="text-[11px] text-slate-600 leading-tight mt-0.5">
                    {v.detail}
                  </div>
                )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Sparkline({
  values,
  type = "line",
  color = "#6d28d9",
  pastBoundary,
  referenceValue,
  tooltipFormatter,
  tooltipSlotProps,
  tooltipInteractive,
}) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const width = 100;
  const height = 28;
  if (!values || values.length === 0) {
    return null;
  }

  const hasReference = referenceValue != null && isFinite(referenceValue);
  const extendedValues = hasReference ? [...values, referenceValue] : values;
  const rawMax = Math.max(...extendedValues);
  const rawMin = Math.min(...extendedValues);
  const span = rawMax - rawMin || 1;
  const padded = span * 0.15;
  const max = rawMax + padded;
  const min = Math.max(0, rawMin - padded);
  const range = max - min || 1;
  const boundary = pastBoundary == null ? values.length : pastBoundary;
  const referenceY = hasReference
    ? height - 1 - ((referenceValue - min) / range) * (height - 2)
    : null;

  const renderOverlay = (slotBoundsFn) =>
    tooltipFormatter ? (
      <div
        className="absolute inset-0"
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {values.map((_, i) => {
          const { left, width: slotWidth } = slotBoundsFn(i);
          return (
            <Tooltip
              key={i}
              title={tooltipFormatter(i)}
              arrow
              placement="top"
              disableInteractive={!tooltipInteractive}
              enterDelay={150}
              enterNextDelay={75}
              slotProps={tooltipSlotProps || lightTooltipSlotProps}
              PopperProps={{
                modifiers: [
                  {
                    name: "preventOverflow",
                    options: { altAxis: true, padding: 8 },
                  },
                ],
              }}
            >
              <div
                className="absolute top-0 h-full cursor-default"
                style={{ left: `${left}%`, width: `${slotWidth}%` }}
                onMouseEnter={() => setHoveredIndex(i)}
              />
            </Tooltip>
          );
        })}
      </div>
    ) : null;

  if (type === "bar") {
    const slot = width / values.length;
    const barWidth = slot * 0.7;
    const minBarHeight = 3;
    const guideX =
      hoveredIndex != null ? hoveredIndex * slot + slot / 2 : null;
    return (
      <div className="relative w-full h-7">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          className="w-full h-full block"
        >
          {values.map((v, i) => {
            const scaled = ((v - min) / range) * (height - minBarHeight);
            const h = Math.max(scaled, 0) + minBarHeight;
            const isZero = v === 0;
            const isFuture = i >= boundary;
            let opacity;
            if (isFuture) opacity = isZero ? 0.15 : 0.3;
            else opacity = isZero ? 0.25 : 0.75;
            return (
              <rect
                key={i}
                x={i * slot + (slot - barWidth) / 2}
                y={height - h}
                width={barWidth}
                height={h}
                fill={color}
                opacity={opacity}
                rx={0.5}
              />
            );
          })}
          {guideX != null && (
            <line
              x1={guideX}
              y1={0}
              x2={guideX}
              y2={height}
              stroke="#94a3b8"
              strokeWidth={0.5}
              vectorEffect="non-scaling-stroke"
              opacity={0.6}
            />
          )}
        </svg>
        {renderOverlay((i) => ({ left: i * slot, width: slot }))}
      </div>
    );
  }

  const stepX = width / (values.length - 1 || 1);
  const splitPast = boundary < values.length;
  const todayIdx = splitPast ? boundary - 1 : -1;
  const now = new Date();
  const dayFraction =
    (now.getHours() * 60 + now.getMinutes()) / (24 * 60);

  const coords = values.map((v, i) => {
    const x = i === todayIdx ? (i + dayFraction) * stepX : i * stepX;
    return {
      x,
      y: height - 1 - ((v - min) / range) * (height - 2),
    };
  });
  const allPoints = coords.map((c) => `${c.x},${c.y}`).join(" ");
  const areaLeadIn =
    splitPast && coords.length > 0 && coords[0].x > 0
      ? `0,${height} 0,${coords[0].y} `
      : `0,${height} `;
  const areaPoints = `${areaLeadIn}${allPoints} ${width},${height}`;

  const pastCoords = splitPast ? coords.slice(0, boundary) : coords;
  const futureCoords = splitPast ? coords.slice(boundary - 1) : [];
  let pastPoints = pastCoords.map((c) => `${c.x},${c.y}`).join(" ");
  if (splitPast && pastCoords.length === 1) {
    const c = pastCoords[0];
    pastPoints = `0,${c.y} ${c.x},${c.y}`;
  }
  const futurePoints = futureCoords.map((c) => `${c.x},${c.y}`).join(" ");

  const hoveredCoord =
    hoveredIndex != null ? coords[hoveredIndex] : null;

  return (
    <div className="relative w-full h-7">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="w-full h-full block"
      >
        <polygon points={areaPoints} fill={color} opacity={0.12} />
        {hasReference && (
          <line
            x1={0}
            y1={referenceY}
            x2={width}
            y2={referenceY}
            stroke="#94a3b8"
            strokeWidth={0.5}
            strokeDasharray="2 2"
            opacity={0.5}
            vectorEffect="non-scaling-stroke"
          />
        )}
        {pastPoints && (
          <polyline
            points={pastPoints}
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            vectorEffect="non-scaling-stroke"
          />
        )}
        {futurePoints && (
          <polyline
            points={futurePoints}
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            strokeDasharray="3 2"
            opacity={0.5}
            vectorEffect="non-scaling-stroke"
          />
        )}
        {hoveredCoord && (
          <line
            x1={hoveredCoord.x}
            y1={0}
            x2={hoveredCoord.x}
            y2={height}
            stroke="#94a3b8"
            strokeWidth={0.5}
            vectorEffect="non-scaling-stroke"
            opacity={0.6}
          />
        )}
      </svg>
      {hoveredCoord && (
        <div
          className="absolute rounded-full pointer-events-none"
          style={{
            left: `${hoveredCoord.x}%`,
            top: `${hoveredCoord.y}px`,
            width: 4,
            height: 4,
            backgroundColor: color,
            transform: "translate(-50%, -50%)",
          }}
        />
      )}
      {renderOverlay((i) => {
        const pointX = coords[i].x;
        const prevX = i > 0 ? coords[i - 1].x : null;
        const nextX = i < coords.length - 1 ? coords[i + 1].x : null;
        const slotLeft = prevX != null ? (prevX + pointX) / 2 : 0;
        const slotRight = nextX != null ? (pointX + nextX) / 2 : width;
        return { left: slotLeft, width: slotRight - slotLeft };
      })}
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  sub,
  subColorClass,
  sparkline,
  sparklineType,
  pastBoundary,
  sparklineReferenceValue,
  sparklineTooltip,
  sparklineTooltipSlotProps,
  sparklineTooltipInteractive,
}) {
  return (
    <div className="flex-1 min-w-[180px] rounded-2xl border border-slate-200 bg-white px-3 pt-2.5 pb-2 flex flex-col">
      <div className="flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-full bg-violet-50 text-violet-700 flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-[11px] text-slate-500 whitespace-nowrap">
            {label}
          </span>
          <span className="text-lg font-semibold text-slate-900 leading-tight whitespace-nowrap">
            {value}
          </span>
          <span
            className={`text-[10px] whitespace-nowrap ${
              subColorClass || "text-slate-400"
            }`}
          >
            {sub}
          </span>
        </div>
      </div>
      <div className="mt-1.5 min-h-[24px]">
        {sparkline && (
          <Sparkline
            values={sparkline}
            type={sparklineType}
            pastBoundary={pastBoundary}
            referenceValue={sparklineReferenceValue}
            tooltipFormatter={sparklineTooltip}
            tooltipSlotProps={sparklineTooltipSlotProps}
            tooltipInteractive={sparklineTooltipInteractive}
          />
        )}
      </div>
    </div>
  );
}

function TooltipBody({ label, value, suffix }) {
  return (
    <div className="px-3 py-2.5 leading-tight">
      <div className="text-[11px] font-semibold text-slate-900">{label}</div>
      <div className="text-xs text-slate-700 mt-0.5">
        {value}
        {suffix && <span className="text-slate-400 ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function WeeklySummaryCards({
  all_data,
  start_date,
  addDays,
  formatDate,
  agg_column,
  filter_df_to_day,
  sum_daily_sales,
  open_shifts,
  countOfWarnings,
  laborLawEnabled,
  laborLawViolations,
  checkedMetricsToggle,
  actualComplianceAudit,
  plannedExpanded,
  setPlannedExpanded,
  cardOrder = DEFAULT_CARD_ORDER,
}) {
  const auditViolations = useMemo(() =>
    (actualComplianceAudit?.data?.groups || []).flatMap((group) =>
      group.violations.map((v) => ({ ...v, regulation_type: group.regulation_type }))
    ), [actualComplianceAudit?.data]);
  const metrics = useMemo(() => {
    const dates = day_nums.map((d) => formatDate(addDays(start_date, d)));
    const today_str = formatDate(new Date(new Date().toDateString()));
    const isPastOrToday = (d) => d <= today_str;
    const isFuture = (d) => d > today_str;
    const isPastDay = isPastOrToday;
    const pastBoundary = dates.findIndex(isFuture);
    const pastOrTodayCount = pastBoundary === -1 ? dates.length : pastBoundary;
    const pastDayCount = pastOrTodayCount;
    const hasPastData = pastOrTodayCount > 0;
    const isFullPastWeek = dates[dates.length - 1] < today_str;

    const weekSales = sum_daily_sales(all_data.forecasted_sales_daily || []);
    const dailySales = dates.map((date_str) => {
      const daily = agg_column(
        filter_df_to_day(all_data.forecasted_sales_daily || [], "date", date_str),
        "value"
      );
      const adjustment = filter_df_to_day(
        all_data.adjustments_chosen || [],
        "date",
        date_str
      );
      const adj = adjustment.length === 0 ? 0 : adjustment[0].adjustment_percent_value;
      return daily * adj + daily;
    });

    const weekTargetHours = agg_column(all_data.forecasted_labor || [], "hours");
    const dailyTargetHours = dates.map((date_str) =>
      agg_column(
        filter_df_to_day(all_data.forecasted_labor || [], "date", date_str),
        "hours"
      )
    );

    const weekScheduledHours = agg_column(
      all_data.actual_schedule_labor_deployment || [],
      "hours"
    );
    const dailyScheduledHours = dates.map((date_str) =>
      agg_column(
        filter_df_to_day(
          all_data.actual_schedule_labor_deployment || [],
          "date",
          date_str
        ),
        "hours"
      )
    );

    const wages = all_data.recent_avg_wages || [];
    const avgWage = wages.length
      ? wages.reduce((a, b) => a + (b.average_wage || 0), 0) / wages.length
      : 0;

    const weekLaborCost = weekScheduledHours * avgWage;
    const dailyLaborCost = dailyScheduledHours.map((h) => h * avgWage);
    const laborPctSales = weekSales > 0 ? (weekLaborCost / weekSales) * 100 : 0;

    const dailyCoverage = dates.map((date_str, i) => {
      const target = dailyTargetHours[i];
      const scheduled = dailyScheduledHours[i];
      if (target <= 0) return 0;
      return (scheduled / target) * 100;
    });
    const weekCoverage =
      weekTargetHours > 0 ? (weekScheduledHours / weekTargetHours) * 100 : 0;

    const dailyOpen = dates.map(
      (date_str) =>
        (open_shifts || []).filter((s) => s.date === date_str).length
    );

    const scheduleById = (all_data.actual_schedule || []).reduce(
      (acc, s) => {
        if (s && s.id != null) acc[s.id] = s;
        return acc;
      },
      {}
    );
    const dailyLaborLawViolationDetails = dates.map((date_str) =>
      (laborLawViolations || [])
        .filter((v) => v?.shifts?.[0]?.date === date_str)
        .map((v) => {
          const allShifts = v.shifts || [];
          const resolved = allShifts
            .map((vs) => buildShiftDetail(vs, scheduleById))
            .filter(Boolean);
          return {
            employeeName: formatName(v.store_team_member_name) || "Unknown",
            regulationName: getRegulationDisplayName(v),
            shifts: resolved,
            unresolvedShiftCount: allShifts.length - resolved.length,
          };
        })
    );
    const dailyLaborLawViolations = dailyLaborLawViolationDetails.map(
      (arr) => arr.length
    );

    const weekDates = new Set(dates);
    const auditInWeek = (auditViolations || []).filter(
      (v) => v?.date && weekDates.has(v.date)
    );
    const dailyActualLaborLawViolationDetails = dates.map((date_str) =>
      auditInWeek
        .filter((v) => v.date === date_str)
        .map((v) => ({
          employeeName: formatName(v.employee_name) || "Unknown",
          regulationName:
            REG_TYPE_NAMES[v.regulation_type] || v.regulation_type,
          detail: v.violation_detail || null,
        }))
    );
    const dailyActualLaborLawViolations =
      dailyActualLaborLawViolationDetails.map((arr) => arr.length);
    const actualLaborLawViolationsCount = auditInWeek.length;

    const actualUsedLabor = all_data.actual_used_labor_vs_earned || [];
    const earnedLabor = all_data.calculated_earned_labor || [];

    const dailyEarnedHours = dates.map((date_str) =>
      agg_column(
        filter_df_to_day(earnedLabor, "date", date_str),
        "hours"
      )
    );

    const dailyActualSales = dates.map((date_str, i) => {
      if (!isPastDay(date_str)) return null;
      return agg_column(
        filter_df_to_day(all_data.actual_sales_daily || [], "date", date_str),
        "value"
      );
    });
    const dailyUsedHours = dates.map((date_str, i) => {
      if (!isPastDay(date_str)) return null;
      return agg_column(
        filter_df_to_day(actualUsedLabor, "date", date_str),
        "hours"
      );
    });
    const dailyPastEarnedHours = dates.map((date_str, i) => {
      if (!isPastDay(date_str)) return null;
      return dailyEarnedHours[i];
    });
    const dailyActualCoverage = dates.map((date_str, i) => {
      if (!isPastDay(date_str)) return null;
      const earned = dailyEarnedHours[i];
      const used = dailyUsedHours[i] || 0;
      if (!earned || earned <= 0) return 0;
      return (used / earned) * 100;
    });

    const dailyHybridSales = dates.map((d, i) =>
      isPastDay(d) ? dailyActualSales[i] || 0 : dailySales[i]
    );
    const dailyHybridUsedHours = dates.map((d, i) =>
      isPastDay(d) ? dailyUsedHours[i] || 0 : dailyScheduledHours[i]
    );
    const dailyHybridEarnedHours = dates.map((d, i) =>
      isPastDay(d) ? dailyEarnedHours[i] || 0 : dailyTargetHours[i]
    );
    const dailyHybridCoverage = dates.map((d, i) =>
      isPastDay(d) ? dailyActualCoverage[i] || 0 : dailyCoverage[i]
    );

    const sum = (arr) => arr.reduce((a, b) => a + (b || 0), 0);
    const pastIndices = dates
      .map((d, i) => (isPastOrToday(d) ? i : -1))
      .filter((i) => i >= 0);
    // Derived rate metrics (SPLH/TPLH/Labor %) compare actuals against a
    // scheduled baseline. Including today distorts that comparison: the
    // scheduled denominator is locked in for the full day, while the actual
    // numerator only covers hours worked so far. Use closed days only for
    // those baselines and rates.
    const closedDayIndices = dates
      .map((d, i) => (d < today_str ? i : -1))
      .filter((i) => i >= 0);
    const hasClosedDays = closedDayIndices.length > 0;

    const actualSalesSoFar = sum(pastIndices.map((i) => dailyActualSales[i]));
    const usedHoursSoFar = sum(pastIndices.map((i) => dailyUsedHours[i]));
    const earnedHoursSoFar = sum(pastIndices.map((i) => dailyEarnedHours[i]));

    const closedActualSales = sum(
      closedDayIndices.map((i) => dailyActualSales[i])
    );
    const closedUsedHours = sum(
      closedDayIndices.map((i) => dailyUsedHours[i])
    );

    const projectedSales = sum(dailyHybridSales);
    const projectedUsedHours = sum(dailyHybridUsedHours);
    const projectedEarnedHours = sum(dailyHybridEarnedHours);

    const salesVariance =
      weekSales > 0 ? ((projectedSales - weekSales) / weekSales) * 100 : 0;
    const usedVsScheduledVariance =
      weekScheduledHours > 0
        ? ((projectedUsedHours - weekScheduledHours) / weekScheduledHours) * 100
        : 0;
    const earnedVsForecastedVariance =
      weekTargetHours > 0
        ? ((projectedEarnedHours - weekTargetHours) / weekTargetHours) * 100
        : 0;

    const closedScheduledHours = sum(
      closedDayIndices.map((i) => dailyScheduledHours[i])
    );
    const pastForecastedHoursPast = sum(
      pastIndices.map((i) => dailyTargetHours[i])
    );
    const pastActualCoverage =
      earnedHoursSoFar > 0 ? (usedHoursSoFar / earnedHoursSoFar) * 100 : 0;

    // Labor cost ($)
    // Use real spent-labor data for past days (matches the per-day metric
    // tooltips that read amount_cents from get_spent_labor_daily_summaries).
    // Multiplying used hours by an averaged recent wage diverges from the
    // tooltip's per-shift wage sum, so we only fall back to that for future
    // days where actuals don't exist yet.
    const spentLaborData = all_data.get_spent_labor_daily_summaries || [];
    const dailyActualLaborCost = dates.map(
      (date_str) =>
        agg_column(
          filter_df_to_day(spentLaborData, "date", date_str),
          "amount_cents"
        ) / 100
    );
    const dailyHybridLaborCost = dates.map((d, i) =>
      isPastDay(d) ? dailyActualLaborCost[i] : dailyLaborCost[i]
    );
    const actualLaborCostSoFar = sum(
      pastIndices.map((i) => dailyActualLaborCost[i])
    );
    const closedActualLaborCost = sum(
      closedDayIndices.map((i) => dailyActualLaborCost[i])
    );
    const projectedLaborCost = sum(dailyHybridLaborCost);
    const laborCostVariance =
      weekLaborCost > 0
        ? ((projectedLaborCost - weekLaborCost) / weekLaborCost) * 100
        : 0;

    // Labor % of sales
    const dailyLaborPct = dates.map((_, i) =>
      dailySales[i] > 0 ? (dailyLaborCost[i] / dailySales[i]) * 100 : 0
    );
    const dailyHybridLaborPct = dates.map((_, i) =>
      dailyHybridSales[i] > 0
        ? (dailyHybridLaborCost[i] / dailyHybridSales[i]) * 100
        : 0
    );
    const actualLaborPctSoFar =
      closedActualSales > 0
        ? (closedActualLaborCost / closedActualSales) * 100
        : 0;
    const projectedLaborPct =
      projectedSales > 0 ? (projectedLaborCost / projectedSales) * 100 : 0;
    const laborPctPpVariance = projectedLaborPct - laborPctSales;
    const closedSalesForecast = sum(
      closedDayIndices.map((i) => dailySales[i])
    );
    const closedLaborCostScheduled = sum(
      closedDayIndices.map((i) => dailyLaborCost[i])
    );
    const scheduledLaborPctSoFar =
      closedSalesForecast > 0
        ? (closedLaborCostScheduled / closedSalesForecast) * 100
        : 0;
    const laborPctSoFarPpVariance =
      actualLaborPctSoFar - scheduledLaborPctSoFar;

    // Transactions
    const dailyForecastedTransactions = dates.map((date_str) =>
      agg_column(
        filter_df_to_day(
          all_data.forecasted_transactions_daily || [],
          "date",
          date_str
        ),
        "value"
      )
    );
    const weekForecastedTransactions = dailyForecastedTransactions.reduce(
      (a, b) => a + (b || 0),
      0
    );
    const actualTransactionRows = (all_data.actual_other_daily || []).filter(
      (r) => r?.metric === "Transaction"
    );
    const dailyActualTransactions = dates.map((date_str) => {
      if (!isPastDay(date_str)) return null;
      return agg_column(
        filter_df_to_day(actualTransactionRows, "date", date_str),
        "value"
      );
    });
    const dailyHybridTransactions = dates.map((d, i) =>
      isPastDay(d)
        ? dailyActualTransactions[i] || 0
        : dailyForecastedTransactions[i]
    );
    const actualTransactionsSoFar = sum(
      pastIndices.map((i) => dailyActualTransactions[i])
    );
    const closedActualTransactions = sum(
      closedDayIndices.map((i) => dailyActualTransactions[i])
    );
    const projectedTransactions = sum(dailyHybridTransactions);
    const transactionsVariance =
      weekForecastedTransactions > 0
        ? ((projectedTransactions - weekForecastedTransactions) /
            weekForecastedTransactions) *
          100
        : 0;

    // SPLH (Sales per Labor Hour)
    const weekSPLH =
      weekScheduledHours > 0 ? weekSales / weekScheduledHours : 0;
    const dailySPLH = dates.map((_, i) =>
      dailyScheduledHours[i] > 0 ? dailySales[i] / dailyScheduledHours[i] : 0
    );
    const dailyHybridSPLH = dates.map((_, i) =>
      dailyHybridUsedHours[i] > 0
        ? dailyHybridSales[i] / dailyHybridUsedHours[i]
        : 0
    );
    const actualSPLHSoFar =
      closedUsedHours > 0 ? closedActualSales / closedUsedHours : 0;
    const projectedSPLH =
      projectedUsedHours > 0 ? projectedSales / projectedUsedHours : 0;
    const splhVariance =
      weekSPLH > 0 ? ((projectedSPLH - weekSPLH) / weekSPLH) * 100 : 0;
    const scheduledSPLHSoFar =
      closedScheduledHours > 0
        ? closedSalesForecast / closedScheduledHours
        : 0;
    const splhSoFarVariance =
      scheduledSPLHSoFar > 0
        ? ((actualSPLHSoFar - scheduledSPLHSoFar) / scheduledSPLHSoFar) * 100
        : 0;

    // Avg Check Size
    const weekAvgCheck =
      weekForecastedTransactions > 0
        ? weekSales / weekForecastedTransactions
        : 0;
    const dailyAvgCheck = dates.map((_, i) =>
      dailyForecastedTransactions[i] > 0
        ? dailySales[i] / dailyForecastedTransactions[i]
        : 0
    );
    const dailyHybridAvgCheck = dates.map((_, i) =>
      dailyHybridTransactions[i] > 0
        ? dailyHybridSales[i] / dailyHybridTransactions[i]
        : 0
    );
    const actualAvgCheckSoFar =
      actualTransactionsSoFar > 0
        ? actualSalesSoFar / actualTransactionsSoFar
        : 0;
    const projectedAvgCheck =
      projectedTransactions > 0 ? projectedSales / projectedTransactions : 0;
    const avgCheckVariance =
      weekAvgCheck > 0
        ? ((projectedAvgCheck - weekAvgCheck) / weekAvgCheck) * 100
        : 0;

    // TPLH (Transactions per Labor Hour)
    const weekTPLH =
      weekScheduledHours > 0
        ? weekForecastedTransactions / weekScheduledHours
        : 0;
    const dailyTPLH = dates.map((_, i) =>
      dailyScheduledHours[i] > 0
        ? dailyForecastedTransactions[i] / dailyScheduledHours[i]
        : 0
    );
    const dailyHybridTPLH = dates.map((_, i) =>
      dailyHybridUsedHours[i] > 0
        ? dailyHybridTransactions[i] / dailyHybridUsedHours[i]
        : 0
    );
    const actualTPLHSoFar =
      closedUsedHours > 0 ? closedActualTransactions / closedUsedHours : 0;
    const projectedTPLH =
      projectedUsedHours > 0 ? projectedTransactions / projectedUsedHours : 0;
    const tplhVariance =
      weekTPLH > 0 ? ((projectedTPLH - weekTPLH) / weekTPLH) * 100 : 0;
    const closedForecastedTransactions = sum(
      closedDayIndices.map((i) => dailyForecastedTransactions[i])
    );
    const scheduledTPLHSoFar =
      closedScheduledHours > 0
        ? closedForecastedTransactions / closedScheduledHours
        : 0;
    const tplhSoFarVariance =
      scheduledTPLHSoFar > 0
        ? ((actualTPLHSoFar - scheduledTPLHSoFar) / scheduledTPLHSoFar) * 100
        : 0;

    // Avg Shift Length
    const scheduledShifts = (all_data.actual_schedule || []).filter(
      (s) =>
        s &&
        s.status !== "AVAILABLE" &&
        s.shift_start != null &&
        s.shift_end != null
    );
    // Backend appends synthetic "no show" placeholders for published shifts
    // that never produced a timecard. Exclude them so shift-count denominators
    // reflect real timecards only.
    // The payload reaches one calendar day past the week so overnight punches
    // on the final business day are not lost. Everything here buckets by the
    // business date the punch belongs to rather than the calendar date it
    // clocked in on, so the week total and the seven daily counts agree and a
    // punch that crosses midnight lands on the day it was worked.
    const usedShiftBusinessDate = (s) => s?.business_date || s?.date_start;
    const usedShifts = (all_data.actual_used_schedule || []).filter((s) => {
      if (!s || s.result_start === "no show") return false;
      const business_date = usedShiftBusinessDate(s);
      return (
        !!business_date &&
        business_date >= dates[0] &&
        business_date <= dates[dates.length - 1]
      );
    });
    const weekScheduledShiftCount = scheduledShifts.length;
    const weekActualShiftCount = usedShifts.length;
    const weekAvgShiftLength =
      weekScheduledShiftCount > 0
        ? weekScheduledHours / weekScheduledShiftCount
        : 0;
    const actualAvgShiftLength =
      weekActualShiftCount > 0 ? usedHoursSoFar / weekActualShiftCount : 0;
    const dailyScheduledShiftCount = dates.map(
      (date_str) =>
        scheduledShifts.filter((s) => s.date === date_str).length
    );
    const dailyAvgShiftLength = dates.map((_, i) =>
      dailyScheduledShiftCount[i] > 0
        ? dailyScheduledHours[i] / dailyScheduledShiftCount[i]
        : 0
    );
    const dailyActualShiftCount = dates.map((date_str) =>
      usedShifts.filter((s) => usedShiftBusinessDate(s) === date_str).length
    );
    const dailyHybridShiftCount = dates.map((d, i) =>
      isPastDay(d) ? dailyActualShiftCount[i] : dailyScheduledShiftCount[i]
    );
    const dailyHybridAvgShiftLength = dates.map((_, i) =>
      dailyHybridShiftCount[i] > 0
        ? dailyHybridUsedHours[i] / dailyHybridShiftCount[i]
        : 0
    );
    const projectedShiftCount = sum(dailyHybridShiftCount);
    const projectedAvgShiftLength =
      projectedShiftCount > 0 ? projectedUsedHours / projectedShiftCount : 0;
    const avgShiftLengthVariance =
      weekAvgShiftLength > 0
        ? ((projectedAvgShiftLength - weekAvgShiftLength) /
            weekAvgShiftLength) *
          100
        : 0;

    // Break Compliance (planned: from laborLawViolations; actual: from auditViolations)
    const breakRegTypes = new Set([
      "meal_break",
      "rest_break",
      "meal_break_timing",
      "rest_break_timing",
    ]);
    const plannedBreakViolationShiftIds = new Set();
    (laborLawViolations || []).forEach((v) => {
      if (!breakRegTypes.has(v?.regulation_type)) return;
      (v.shifts || []).forEach((s) => {
        if (s?.id != null) plannedBreakViolationShiftIds.add(s.id);
      });
    });
    const breakEligibleShifts = (all_data.actual_schedule || []).filter(
      (s) => s && s.shift_start != null && s.shift_end != null
    );
    const plannedBreakComplianceCount = Math.max(
      0,
      breakEligibleShifts.length - plannedBreakViolationShiftIds.size
    );
    const plannedBreakCompliancePct =
      breakEligibleShifts.length > 0
        ? (plannedBreakComplianceCount / breakEligibleShifts.length) * 100
        : 100;
    const dailyPlannedBreakCompliance = dates.map((date_str) => {
      const dayShifts = breakEligibleShifts.filter((s) => s.date === date_str).length;
      if (dayShifts <= 0) return 100;
      const violatedDayShifts = new Set();
      (laborLawViolations || []).forEach((v) => {
        if (!breakRegTypes.has(v?.regulation_type)) return;
        (v.shifts || []).forEach((s) => {
          if (s?.date === date_str && s?.id != null) {
            violatedDayShifts.add(s.id);
          }
        });
      });
      return ((dayShifts - violatedDayShifts.size) / dayShifts) * 100;
    });
    // Dedupe break audit violations to a unique shift via the timecard's
    // clocked_in_at/clocked_out_at, so multiple break-rule failures on the
    // same shift count once and split-shift / multi-timecard days are not
    // collapsed into a single employee-day bucket.
    const violatedShiftKey = (v) =>
      `${v.employee_name || ""}|${v.clocked_in_at || ""}|${
        v.clocked_out_at || ""
      }`;
    const actualBreakViolationKeys = new Set();
    auditInWeek.forEach((v) => {
      if (!breakRegTypes.has(v?.regulation_type)) return;
      actualBreakViolationKeys.add(violatedShiftKey(v));
    });
    const actualBreakViolationCount = actualBreakViolationKeys.size;
    const actualBreakCompliancePct =
      weekActualShiftCount > 0
        ? Math.max(
            0,
            ((weekActualShiftCount - actualBreakViolationCount) /
              weekActualShiftCount) *
              100
          )
        : 100;
    const dailyActualBreakCompliance = dates.map((date_str, i) => {
      if (!isPastDay(date_str)) return null;
      const dayTimecards = dailyActualShiftCount[i];
      if (dayTimecards <= 0) return 100;
      const dayViolatedShifts = new Set();
      auditInWeek.forEach((v) => {
        if (v.date !== date_str) return;
        if (!breakRegTypes.has(v?.regulation_type)) return;
        dayViolatedShifts.add(violatedShiftKey(v));
      });
      return Math.max(
        0,
        ((dayTimecards - dayViolatedShifts.size) / dayTimecards) * 100
      );
    });

    return {
      weekSales,
      dailySales,
      weekTargetHours,
      dailyTargetHours,
      weekScheduledHours,
      dailyScheduledHours,
      weekEarnedHours: agg_column(earnedLabor, "hours"),
      dailyEarnedHours,
      weekCoverage,
      dailyCoverage,
      dailyOpen,
      dailyLaborLawViolations,
      hasPastData,
      hasClosedDays,
      isFullPastWeek,
      pastBoundary: pastDayCount,
      actualSalesSoFar,
      projectedSales,
      usedHoursSoFar,
      projectedUsedHours,
      earnedHoursSoFar,
      projectedEarnedHours,
      weekActualCoverage: pastActualCoverage,
      dailyHybridSales,
      dailyHybridUsedHours,
      dailyHybridEarnedHours,
      dailyHybridCoverage,
      salesVariance,
      usedVsScheduledVariance,
      earnedVsForecastedVariance,
      actualLaborLawViolationsCount,
      dailyActualLaborLawViolations,
      dailyLaborLawViolationDetails,
      dailyActualLaborLawViolationDetails,

      // Labor cost
      weekLaborCost,
      dailyLaborCost,
      dailyHybridLaborCost,
      actualLaborCostSoFar,
      projectedLaborCost,
      laborCostVariance,

      // Labor %
      laborPctSales,
      dailyLaborPct,
      dailyHybridLaborPct,
      actualLaborPctSoFar,
      projectedLaborPct,
      laborPctPpVariance,
      laborPctSoFarPpVariance,

      // Transactions
      weekForecastedTransactions,
      dailyForecastedTransactions,
      dailyHybridTransactions,
      actualTransactionsSoFar,
      projectedTransactions,
      transactionsVariance,

      // SPLH
      weekSPLH,
      dailySPLH,
      dailyHybridSPLH,
      actualSPLHSoFar,
      projectedSPLH,
      splhVariance,
      splhSoFarVariance,

      // Avg Check
      weekAvgCheck,
      dailyAvgCheck,
      dailyHybridAvgCheck,
      actualAvgCheckSoFar,
      projectedAvgCheck,
      avgCheckVariance,

      // TPLH
      weekTPLH,
      dailyTPLH,
      dailyHybridTPLH,
      actualTPLHSoFar,
      projectedTPLH,
      tplhVariance,
      tplhSoFarVariance,

      // Avg Shift Length
      weekAvgShiftLength,
      dailyAvgShiftLength,
      dailyHybridAvgShiftLength,
      actualAvgShiftLength,
      projectedAvgShiftLength,
      avgShiftLengthVariance,

      // Break Compliance
      plannedBreakCompliancePct,
      dailyPlannedBreakCompliance,
      actualBreakCompliancePct,
      dailyActualBreakCompliance,

      // Availability Conflicts (per-day)
      dailyConflicts: countOfWarnings?.dailyConflicts || [
        0, 0, 0, 0, 0, 0, 0,
      ],
      dailyConflictDetails: (
        countOfWarnings?.dailyConflictDetails || [[], [], [], [], [], [], []]
      ).map((dayConflicts) =>
        (dayConflicts || []).map((c) => {
          const start = get_t_str_shortened(c.shift?.shift_start);
          const end = get_t_str_shortened(c.shift?.shift_end);
          const hasTimes =
            c.shift?.shift_start != null && c.shift?.shift_end != null;
          return {
            employeeName: c.employeeName || "Unknown",
            regulationName: c.reason || "Conflict",
            shifts: hasTimes
              ? [{ timeRange: `${start}–${end}`, breaks: [] }]
              : [],
          };
        })
      ),
    };
  }, [
    all_data,
    start_date,
    addDays,
    formatDate,
    agg_column,
    filter_df_to_day,
    sum_daily_sales,
    open_shifts,
    laborLawViolations,
    auditViolations,
    countOfWarnings,
  ]);

  const conflictsCount = countOfWarnings?.countOfConflicts || 0;
  const laborLawViolationsCount = laborLawViolations?.length || 0;
  const showLaborLawCard = !!laborLawEnabled;
  const openShiftsCount = (open_shifts || []).length;

  const iconStyle = { fontSize: "18px" };

  const showActualRow = !!checkedMetricsToggle && metrics.hasPastData;

  const dateLabels = useMemo(
    () =>
      day_nums.map((d) =>
        addDays(start_date, d).toLocaleDateString("en-us", {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
      ),
    [start_date, addDays]
  );

  const cardTooltip = (formatter, suffix) => (i) =>
    (
      <TooltipBody
        label={dateLabels[i]}
        value={formatter(i)}
        suffix={suffix}
      />
    );

  const richViolationTooltip = (perDayDetails) => (i) =>
    (
      <ViolationTooltip
        dateLabel={dateLabels[i]}
        violations={perDayDetails[i] || []}
      />
    );

  const ctx = {
    showLaborLawCard,
    conflictsCount,
    laborLawViolationsCount,
    laborLawEnabled: !!laborLawEnabled,
    openShiftsCount,
  };

  const renderCardSide = (id, side) => {
    const entry = SUMMARY_CARD_REGISTRY[id];
    if (!entry) {
      return (
        <div
          key={`${id}-${side}-missing`}
          className="flex-1 min-w-[180px]"
        />
      );
    }
    // Feature flag gates the entire card. Saved preferences may carry a
    // gated card from a store where the feature was on; in a store where
    // it's off, render a "Not available" placeholder on planned and an
    // empty slot on actual rather than misleading values.
    if (entry.featureFlag && !ctx[entry.featureFlag]) {
      if (side === "actual") {
        return (
          <div
            key={`${id}-${side}-flag-gated`}
            className="flex-1 min-w-[180px]"
          />
        );
      }
      const Icon = entry.icon;
      return (
        <div
          key={`${id}-${side}-flag-gated`}
          className="flex-1 min-w-[180px] rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-3 pt-2.5 pb-2 flex flex-col"
        >
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0">
              <Icon style={iconStyle} />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-[11px] text-slate-400 whitespace-nowrap">
                {entry.label}
              </span>
              <span className="text-sm font-medium text-slate-400 leading-tight whitespace-nowrap">
                Not available
              </span>
              <span className="text-[10px] text-slate-400 whitespace-nowrap">
                Requires labor law
              </span>
            </div>
          </div>
        </div>
      );
    }
    if (side === "actual" && entry.actualGated && !ctx[entry.actualGated]) {
      return (
        <div
          key={`${id}-${side}-gated`}
          className="flex-1 min-w-[180px]"
        />
      );
    }
    const config = entry[side];
    if (!config) {
      const Icon = entry.icon;
      return (
        <div
          key={`${id}-${side}-na`}
          className="flex-1 min-w-[180px] rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-3 pt-2.5 pb-2 flex flex-col"
        >
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0">
              <Icon style={iconStyle} />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-[11px] text-slate-400 whitespace-nowrap">
                {entry.label}
              </span>
              <span className="text-sm font-medium text-slate-400 leading-tight whitespace-nowrap">
                Planned only
              </span>
              <span className="text-[10px] text-slate-400 whitespace-nowrap">
                No actual equivalent
              </span>
            </div>
          </div>
        </div>
      );
    }

    let sparklineTooltip;
    let sparklineTooltipInteractive = false;
    if (config.sparklineTooltip) {
      const t = config.sparklineTooltip;
      if (t.kind === "value") {
        sparklineTooltip = cardTooltip(
          (i) => t.formatter(metrics, i, ctx),
          t.suffix
        );
      } else if (t.kind === "violations") {
        const enabled = t.when ? t.when(metrics, ctx) : true;
        if (enabled) {
          sparklineTooltip = richViolationTooltip(t.details(metrics, ctx));
          // Long violation lists scroll inside the tooltip, so it must stay
          // open while the cursor is over it.
          sparklineTooltipInteractive = true;
        }
      }
    }

    const Icon = entry.icon;

    if (side === "actual" && ["violations", "break_compliance"].includes(id) && !actualComplianceAudit?.data) {
      return (
        <SummaryCard key={`${id}-${side}`} icon={<Icon style={iconStyle} />}
          label={config.cardLabel(metrics, ctx)}
          value={actualComplianceAudit?.error ? "Unavailable" : "Checking…"}
          sub="From timecards" />
      );
    }

    return (
      <SummaryCard
        key={`${id}-${side}`}
        icon={<Icon style={iconStyle} />}
        label={config.cardLabel(metrics, ctx)}
        value={config.value(metrics, ctx)}
        sub={config.sub ? config.sub(metrics, ctx) : undefined}
        subColorClass={
          config.subColorClass ? config.subColorClass(metrics, ctx) : undefined
        }
        sparkline={
          config.sparkline ? config.sparkline(metrics, ctx) : undefined
        }
        sparklineType={config.sparklineType}
        sparklineReferenceValue={config.sparklineReferenceValue}
        pastBoundary={
          config.hasPastBoundary ? metrics.pastBoundary : undefined
        }
        sparklineTooltip={sparklineTooltip}
        sparklineTooltipInteractive={sparklineTooltipInteractive}
      />
    );
  };

  const plannedRow = (
    <div className="flex gap-3 overflow-x-auto">
      {cardOrder.map((id) => renderCardSide(id, "planned"))}
    </div>
  );

  const actualRow = showActualRow && (
    <div className="flex gap-3 overflow-x-auto">
      {cardOrder.map((id) => renderCardSide(id, "actual"))}
    </div>
  );

  const expandToggle = showActualRow && (
    <div className="flex justify-start">
      <button
        type="button"
        onClick={() => setPlannedExpanded((v) => !v)}
        aria-expanded={plannedExpanded}
        className="inline-flex items-center gap-0.5 text-[11px] text-slate-500 hover:text-slate-800 px-2 py-0.5 rounded-md hover:bg-slate-100 transition-colors"
      >
        {plannedExpanded ? "Hide planned" : "Show planned"}
        {plannedExpanded ? (
          <ExpandLessIcon style={{ fontSize: 16 }} />
        ) : (
          <ExpandMoreIcon style={{ fontSize: 16 }} />
        )}
      </button>
    </div>
  );

  return (
    <div
      className={`flex flex-col gap-2 pb-4 ${
        showActualRow ? "pt-0.5" : "pt-2"
      }`}
    >
      {showActualRow ? (
        <>
          {expandToggle}
          {plannedExpanded && plannedRow}
          {actualRow}
        </>
      ) : (
        plannedRow
      )}
    </div>
  );
}

export default WeeklySummaryCards;
